import React, { useEffect, useState } from "react";
import { api, inr, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash, PencilSimple, ArrowUpRight, ArrowDownLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageExportBar from "@/components/PageExportBar";
import SearchableCustomerSelect from "@/components/SearchableCustomerSelect";
import { openPrintStatement } from "@/lib/printStatement";
import { Printer } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);

function EditVoucherDialog({ voucher, onClose, onSaved }) {
  const { dateFloor } = useAuth();
  const [date, setDate] = useState(voucher?.date || today());
  const [amount, setAmount] = useState(voucher?.amount || 0);
  const [mode, setMode] = useState(voucher?.mode || "cash");
  const [reference, setReference] = useState(voucher?.reference || "");
  const [partyName, setPartyName] = useState(voucher?.party_name || "");
  const [notes, setNotes] = useState(voucher?.notes || "");
  const [saving, setSaving] = useState(false);

  const isReceipt = voucher?.type === "receipt";
  const amountChanged = Math.abs(Number(amount) - Number(voucher?.amount || 0)) > 0.001;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/payments/${voucher.id}`, {
        date, mode, reference,
        party_name: partyName,
        notes,
        amount: Number(amount),
      });
      toast.success("Voucher updated" + (amountChanged ? " — allocations re-run FIFO on new amount" : ""));
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!voucher} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="voucher-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit {isReceipt ? "Credit Voucher" : "Debit Voucher"} · {voucher?.number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Date</Label>
            <Input type="date" value={date} min={dateFloor} onChange={(e) => setDate(e.target.value)} className="font-mono" data-testid="edit-voucher-date" />
          </div>
          <div>
            <Label>{isReceipt ? "Customer name" : "Supplier name"}</Label>
            <Input value={partyName} onChange={(e) => setPartyName(e.target.value)} data-testid="edit-voucher-party" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="font-mono" data-testid="edit-voucher-amount" />
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="edit-voucher-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Reference</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="edit-voucher-ref" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          {amountChanged && (
            <div className="text-[11.5px] text-warning bg-warning/10 border border-warning/20 rounded p-2">
              Amount changed → the old allocations will be reversed and re-applied FIFO to the party's oldest open dues automatically.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-voucher-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Common business-expense categories. Picked once per voucher so P&L
// can aggregate "Salary", "Rent", … totals even though we're reusing the
// payment_vouchers collection.
const EXPENSE_HEADS = [
  "Salary", "Advance Salary", "Rent", "Electricity", "Water", "Internet",
  "Telephone", "Travel & Conveyance", "Fuel", "Printing & Stationery",
  "Office Expense", "Repairs & Maintenance", "Bank Charges",
  "Professional Fees", "Marketing", "Courier & Postage", "Food & Refreshment",
  "Government Fees", "Other",
];

function ExpenseVoucherForm({ onClose, onSaved }) {
  const { dateFloor } = useAuth();
  const [date, setDate] = useState(today());
  const [head, setHead] = useState("Salary");
  const [payee, setPayee] = useState("");
  const [mode, setMode] = useState("cash");
  const [amount, setAmount] = useState(0);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // Default salary period = the previous calendar month, since salary is
  // almost always paid in the first week of the next month.
  const prevMonth = () => {
    const d = new Date();
    d.setDate(1); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const [salaryMonth, setSalaryMonth] = useState(prevMonth());
  const isSalary = head === "Salary" || head === "Advance Salary";

  const submit = async (e) => {
    e.preventDefault();
    if (!payee.trim()) { toast.error("Enter payee / description"); return; }
    if (!amount || amount <= 0) { toast.error("Enter amount"); return; }
    if (isSalary && !salaryMonth) { toast.error("Pick the salary month"); return; }
    setSaving(true);
    try {
      await api.post("/payments", {
        type: "payment",
        date,
        party_id: "",
        party_name: payee.trim(),
        party_type: "expense",
        expense_head: head,
        salary_month: isSalary ? salaryMonth : "",
        mode, reference, amount: Number(amount), notes,
        allocations: [],
      });
      toast.success("Expense recorded");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="eyebrow">Expense Head *</Label>
          <Select value={head} onValueChange={setHead}>
            <SelectTrigger data-testid="expense-head"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EXPENSE_HEADS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Paid To / Description *</Label>
          <Input value={payee} onChange={(e) => setPayee(e.target.value)}
                 placeholder="e.g. Rajesh (Salary Feb-26) · Electricity Bill · MSEB"
                 data-testid="expense-payee" />
        </div>
        {isSalary && (
          <div className="space-y-1.5">
            <Label className="eyebrow">Salary Month *</Label>
            <Input type="month" value={salaryMonth}
                   onChange={(e) => setSalaryMonth(e.target.value)}
                   className="font-mono" data-testid="expense-salary-month" />
            <div className="text-[10.5px] text-muted-foreground">
              The month this salary is for (payment date can be in the next month).
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="eyebrow">Date</Label>
          <Input type="date" min={dateFloor} value={date} onChange={(e) => setDate(e.target.value)} className="font-mono" />
          {isSalary && (
            <div className="text-[10.5px] text-muted-foreground">
              Date when the salary is actually paid.
            </div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Mode</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="expense-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="bank">Bank Transfer</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
              <SelectItem value="neft">NEFT / RTGS</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Amount (₹) *</Label>
          <Input required type="number" step="0.01" value={amount}
                 onChange={(e) => setAmount(e.target.value)} className="font-mono"
                 data-testid="expense-amount" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Reference</Label>
          <Input value={reference} onChange={(e) => setReference(e.target.value)}
                 placeholder="Cheque # · UPI txn id · Bill #" className="font-mono" />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label className="eyebrow">Notes</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional context — month, period, etc." />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="expense-save">
          {saving ? "Saving…" : "Save Expense Voucher"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function VoucherForm({ type, onClose, onSaved }) {
  const isReceipt = type === "receipt";
  const { dateFloor } = useAuth();
  const [parties, setParties] = useState([]);
  const [party, setParty] = useState(null);
  const [outstanding, setOutstanding] = useState([]);
  const [date, setDate] = useState(today());
  const [mode, setMode] = useState("cash");
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState("");
  const [allocations, setAllocations] = useState({}); // {invoiceId: amount}
  const [autoAlloc, setAutoAlloc] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(isReceipt ? "/customers" : "/suppliers").then((r) => setParties(r.data));
  }, [isReceipt]);

  useEffect(() => {
    if (!party) { setOutstanding([]); return; }
    api.get(`/ledger/${isReceipt ? "customer" : "supplier"}/${party.id}/outstanding`)
      .then((r) => setOutstanding(r.data));
  }, [party, isReceipt]);

  // FIFO auto-allocation: whenever amount or outstanding list changes,
  // fill oldest invoice first, then spill over into the next until amount exhausts.
  useEffect(() => {
    if (!autoAlloc) return;
    let remaining = Number(amount) || 0;
    const next = {};
    outstanding.forEach((o) => {
      if (remaining <= 0) return;
      const take = Math.min(Number(o.balance_due) || 0, remaining);
      if (take > 0) next[o.id] = Number(take.toFixed(2));
      remaining -= take;
    });
    setAllocations(next);
  }, [amount, outstanding, autoAlloc]);

  const allocSum = Object.values(allocations).reduce((s, v) => s + (Number(v) || 0), 0);
  const remaining = (Number(amount) || 0) - allocSum;

  const manualAllocate = (invoiceId, value) => {
    // any manual change disables auto-allocation
    setAutoAlloc(false);
    setAllocations({ ...allocations, [invoiceId]: value });
  };

  const resetAuto = () => {
    setAutoAlloc(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!party) { toast.error("Select a party"); return; }
    if (!amount || amount <= 0) { toast.error("Enter amount"); return; }
    setSaving(true);
    try {
      const body = {
        type, date, party_id: party.id, party_name: party.name,
        mode, reference, amount: Number(amount), notes,
        allocations: Object.entries(allocations)
          .filter(([, v]) => Number(v) > 0)
          .map(([invoice_id, amt]) => {
            const o = outstanding.find((x) => x.id === invoice_id);
            return {
              invoice_id,
              invoice_number: o?.number || "",
              amount: Number(amt),
              kind: o?.kind || "invoice",
            };
          }),
      };
      await api.post("/payments", body);
      toast.success(`${isReceipt ? "Credit Voucher" : "Debit Voucher"} recorded`);
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 space-y-1.5">
          <Label className="eyebrow">{isReceipt ? "Customer" : "Supplier"} *</Label>
          <SearchableCustomerSelect
            value={party?.id || ""}
            onChange={(v) => setParty(parties.find((p) => p.id === v))}
            customers={parties}
            placeholder={`Search & select ${isReceipt ? "customer" : "supplier"}`}
            testid="voucher-party"
            getSubtitle={(p) => p.gstin || p.phone}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Date</Label>
          <Input type="date" min={dateFloor} value={date} onChange={(e) => setDate(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Mode</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="bank">Bank Transfer</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
              <SelectItem value="neft">NEFT / RTGS</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Amount (₹) *</Label>
          <Input required type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="font-mono" data-testid="voucher-amount" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Reference</Label>
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Cheque #, UPI txn id, …" className="font-mono" />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label className="eyebrow">Notes</Label>
          <Textarea rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      {outstanding.length > 0 && (
        <div className="card-flat overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <div className="eyebrow">
              Allocate to Outstanding Invoices
              <span className="ml-2 text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
                {autoAlloc ? "· Auto-filling oldest first (FIFO)" : "· Manual mode"}
              </span>
            </div>
            <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]"
              onClick={resetAuto} disabled={autoAlloc} data-testid="voucher-reset-auto">
              Reset to Auto (FIFO)
            </Button>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Doc # <span className="text-[10px] font-normal normal-case text-muted-foreground">(Invoice / Temp)</span></th>
                <th>Date</th>
                <th className="num">Grand Total</th>
                <th className="num">Balance Due</th>
                <th className="num">Allocate</th>
              </tr>
            </thead>
            <tbody>
              {outstanding.map((o) => (
                <tr key={o.id}>
                  <td className="font-mono text-[12px]">
                    {o.number}
                    {o.kind === "temp" && (
                      <span className="ml-1.5 inline-block align-middle text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        Temp
                      </span>
                    )}
                  </td>
                  <td>{o.date}</td>
                  <td className="num">{inr(o.grand_total)}</td>
                  <td className="num">{inr(o.balance_due)}</td>
                  <td className="num">
                    <Input type="number" step="0.01" max={o.balance_due}
                      value={allocations[o.id] || ""}
                      onChange={(e) => manualAllocate(o.id, e.target.value)}
                      className="h-8 text-right font-mono w-32 ml-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2.5 border-t border-border flex justify-between text-[12px] font-mono">
            <span className="text-muted-foreground">Allocated: {inr(allocSum)}</span>
            <span className={remaining < 0 ? "text-destructive" : "text-muted-foreground"}>
              {remaining >= 0 ? "On-account balance:" : "Over-allocated by:"} {inr(Math.abs(remaining))}
            </span>
          </div>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="voucher-save">
          {saving ? "Saving…" : `Save ${isReceipt ? "Credit Voucher" : "Debit Voucher"}`}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function Payments() {
  const { activeCompanyId, activeCompany, activeStoreId, dateFloor, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("receipt");
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(today());
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [payeeFilter, setPayeeFilter] = useState("all");

  // Expenses-tab helpers — unique payee list and per-payee aggregates.
  const expensePayees = React.useMemo(() => {
    if (tab !== "expense") return [];
    const seen = new Map();
    rows.forEach((r) => {
      const name = (r.party_name || "").trim();
      if (!name) return;
      seen.set(name, (seen.get(name) || 0) + 1);
    });
    return Array.from(seen.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, tab]);

  const filteredRows = React.useMemo(() => {
    const inRange = rows.filter((r) => {
      const d = (r.date || "").slice(0, 10);
      return (!start || d >= start) && (!end || d <= end);
    });
    if (tab !== "expense" || payeeFilter === "all") return inRange;
    const needle = payeeFilter.toLowerCase();
    return inRange.filter((r) => (r.party_name || "").toLowerCase() === needle);
  }, [rows, start, end, tab, payeeFilter]);

  // When tab changes, reset payee filter so expense selection doesn't leak
  // into non-expense tabs.
  useEffect(() => { setPayeeFilter("all"); }, [tab]);

  // Group totals by payee for the Expenses tab footer.
  const payeeTotals = React.useMemo(() => {
    if (tab !== "expense") return [];
    const map = new Map();
    filteredRows.forEach((r) => {
      const name = (r.party_name || "—").trim() || "—";
      const row = map.get(name) || { name, count: 0, total: 0, byHead: {} };
      row.count += 1;
      row.total += Number(r.amount || 0);
      const head = r.expense_head || "Other";
      row.byHead[head] = (row.byHead[head] || 0) + Number(r.amount || 0);
      map.set(name, row);
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [filteredRows, tab]);

  const load = async () => {
    setLoading(true);
    try {
      // Expenses tab = debit vouchers with party_type="expense";
      // Receipt / Payment tabs = everything else of that type.
      const params = tab === "expense"
        ? { type: "payment", party_type: "expense" }
        : { type: tab, party_type: tab === "receipt" ? "customer" : "supplier" };
      const qs = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
      const { data } = await api.get(`/payments?${qs}`);
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId, tab]);

  const del = async (id) => {
    if (!confirm("Delete this voucher? Linked invoices will be reversed.")) return;
    try { await api.delete(`/payments/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const exportColumns = [
    { key: "number", label: "Voucher #" },
    { key: "date", label: "Date" },
    { key: "party_name", label: tab === "receipt" ? "From (Customer)" : "To (Supplier)" },
    { key: "mode", label: "Mode" },
    { key: "reference", label: "Reference" },
    { key: "amount", label: "Amount" },
  ];

  // Print a client-facing statement of the currently visible vouchers, grouped by party.
  const printStatement = () => {
    if (!filteredRows.length) { toast.error("Nothing to print"); return; }
    const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const groups = new Map();
    for (const r of filteredRows) {
      const key = r.party_id || r.party_name || "unknown";
      if (!groups.has(key)) groups.set(key, { name: r.party_name || "—", rows: [] });
      groups.get(key).rows.push(r);
    }
    const grandTotal = filteredRows.reduce((s, r) => s + Number(r.amount || 0), 0);
    const sections = Array.from(groups.values()).map((g) => {
      const partyTotal = g.rows.reduce((s, r) => s + Number(r.amount || 0), 0);
      return {
        heading: g.name,
        subheading: `${g.rows.length} voucher${g.rows.length > 1 ? "s" : ""} · Total <b>${fmtInr(partyTotal)}</b>`,
        columns: [
          { key: "number", label: "Voucher #", mono: true },
          { key: "date", label: "Date", mono: true },
          { key: "mode", label: "Mode" },
          { key: "reference", label: "Reference" },
          { key: "amount", label: "Amount", align: "right", mono: true },
        ],
        rows: g.rows.map((r) => ({
          number: r.number, date: r.date, mode: (r.mode || "").toUpperCase(),
          reference: r.reference || "", amount: fmtInr(r.amount),
        })),
      };
    });
    const kind = tab === "receipt" ? "Credit Voucher" : "Debit Voucher";
    const res = openPrintStatement({
      title: `${kind} Statement (${start} → ${end})`,
      company: activeCompany || {},
      sections,
      totals: [
        { label: `Total ${tab === "receipt" ? "Received" : "Paid"}`, value: fmtInr(grandTotal), tone: tab === "receipt" ? "pos" : "neg" },
        { label: "Voucher Count", value: String(filteredRows.length) },
      ],
      note: tab === "receipt"
        ? "Thank you — please retain this statement for your records."
        : "Payment statement for supplier reconciliation.",
    });
    if (!res.ok) toast.error(res.error);
    else toast.success(`Preparing statement for ${filteredRows.length} voucher${filteredRows.length > 1 ? "s" : ""}…`);
  };

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Accounting</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Debit &amp; Credit Vouchers</h1>
          <p className="text-sm text-muted-foreground mt-1">Credit vouchers (money in) from customers and debit vouchers (money out) to suppliers — invoices auto-settle from allocations.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="h-9" onClick={printStatement} data-testid="vouchers-print-statement">
            <Printer size={16} className="mr-1.5" /> Print Statement
          </Button>
          <PageExportBar
            title="Payment Vouchers"
            filename={`${tab === "receipt" ? "credit-vouchers" : "debit-vouchers"}-${new Date().toISOString().slice(0,10)}`}
            columns={exportColumns}
            rows={filteredRows}
            testidPrefix="vouchers"
          />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-9" data-testid="voucher-new-btn">
                <Plus size={15} weight="bold" className="mr-1.5" /> New {tab === "receipt" ? "Credit Voucher" : tab === "expense" ? "Expense Voucher" : "Debit Voucher"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
                  {tab === "receipt" ? (
                    <><ArrowDownLeft size={20} style={{ color: "hsl(var(--success))" }} /> New Credit Voucher (Money In)</>
                  ) : tab === "expense" ? (
                    <><ArrowUpRight size={20} className="text-primary" /> New Expense Voucher (Money Out)</>
                  ) : (
                    <><ArrowUpRight size={20} className="text-primary" /> New Debit Voucher (Money Out)</>
                  )}
                </DialogTitle>
              </DialogHeader>
              {tab === "expense"
                ? <ExpenseVoucherForm onClose={() => setOpen(false)} onSaved={load} />
                : <VoucherForm type={tab} onClose={() => setOpen(false)} onSaved={load} />}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="voucher-tabs">
          <TabsTrigger value="receipt" data-testid="tab-receipts">Credit Voucher (Money In)</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payments">Debit Voucher (Money Out)</TabsTrigger>
          <TabsTrigger value="expense" data-testid="tab-expenses">Expenses</TabsTrigger>
        </TabsList>

        <div className="card-flat p-3 flex flex-wrap items-end gap-3 mt-4 no-print">
          <div className="space-y-1.5">
            <Label className="eyebrow">From Date</Label>
            <Input type="date" min="1900-01-01" value={start} onChange={(e) => setStart(e.target.value)}
              className="h-9 font-mono w-40" data-testid="vouchers-start" />
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow">To Date</Label>
            <Input type="date" min="1900-01-01" value={end} onChange={(e) => setEnd(e.target.value)}
              className="h-9 font-mono w-40" data-testid="vouchers-end" />
          </div>
          <Button variant="outline" size="sm" className="h-9"
            onClick={() => { const d = new Date(); d.setDate(d.getDate() - 30); setStart(d.toISOString().slice(0,10)); setEnd(today()); }}
            data-testid="vouchers-30d-btn">
            Last 30 days
          </Button>
          <Button variant="outline" size="sm" className="h-9"
            onClick={() => { setStart(""); setEnd(""); }}
            data-testid="vouchers-clear-btn">
            Clear
          </Button>
          {tab === "expense" && (
            <div className="space-y-1.5">
              <Label className="eyebrow">Paid To</Label>
              <Select value={payeeFilter} onValueChange={setPayeeFilter}>
                <SelectTrigger className="h-9 w-56" data-testid="expense-payee-filter">
                  <SelectValue placeholder="All payees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All payees</SelectItem>
                  {expensePayees.map((p) => (
                    <SelectItem key={p.name} value={p.name}>
                      {p.name} <span className="text-muted-foreground">· {p.count}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="text-[12px] text-muted-foreground ml-auto">
            Showing {filteredRows.length} of {rows.length} vouchers
          </div>
        </div>

        <TabsContent value="receipt" className="mt-4">
          <div className="card-flat overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Voucher #</th>
                  <th>Date</th>
                  <th>{tab === "receipt" ? "From (Customer)" : "To (Supplier)"}</th>
                  <th>Mode</th>
                  <th>Reference</th>
                  <th className="num">Amount</th>
                  <th className="num" style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
                ) : filteredRows.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">
                    No {tab === "receipt" ? "credit vouchers" : "debit vouchers"} in the selected date range.
                  </td></tr>
                ) : filteredRows.map((r) => (
                  <tr key={r.id} data-testid={`voucher-row-${r.id}`}>
                    <td className="font-mono text-[12.5px]">{r.number}</td>
                    <td>{fmtDate(r.date)}</td>
                    <td className="font-medium">{r.party_name}</td>
                    <td><span className="pill pill-muted">{r.mode}</span></td>
                    <td className="font-mono text-[12px] text-muted-foreground">{r.reference || "—"}</td>
                    <td className="num font-semibold">{inr(r.amount)}</td>
                    <td className="num" style={{ textAlign: "right" }}>
                      {user?.role === "admin" && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(r)} title="Edit voucher (admin only)" data-testid={`voucher-edit-${r.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)}>
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payment" className="mt-4">
          <div className="card-flat overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Voucher #</th>
                  <th>Date</th>
                  <th>To (Supplier)</th>
                  <th>Mode</th>
                  <th>Reference</th>
                  <th className="num">Amount</th>
                  <th className="num" style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
                ) : filteredRows.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">
                    No debit vouchers in the selected date range.
                  </td></tr>
                ) : filteredRows.map((r) => (
                  <tr key={r.id} data-testid={`voucher-row-${r.id}`}>
                    <td className="font-mono text-[12.5px]">{r.number}</td>
                    <td>{fmtDate(r.date)}</td>
                    <td className="font-medium">{r.party_name}</td>
                    <td><span className="pill pill-muted">{r.mode}</span></td>
                    <td className="font-mono text-[12px] text-muted-foreground">{r.reference || "—"}</td>
                    <td className="num font-semibold">{inr(r.amount)}</td>
                    <td className="num" style={{ textAlign: "right" }}>
                      {user?.role === "admin" && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(r)} title="Edit voucher (admin only)" data-testid={`voucher-edit-${r.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)}>
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="expense" className="mt-4">
          <div className="card-flat overflow-hidden">
            <table className="data-table" data-testid="expenses-table">
              <thead>
                <tr>
                  <th>Voucher #</th>
                  <th>Date</th>
                  <th>Expense Head</th>
                  <th>Paid To</th>
                  <th>Mode</th>
                  <th>Reference</th>
                  <th className="num">Amount</th>
                  <th className="num" style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
                ) : filteredRows.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">
                    No expense vouchers in the selected date range.
                  </td></tr>
                ) : filteredRows.map((r) => (
                  <tr key={r.id} data-testid={`expense-row-${r.id}`}>
                    <td className="font-mono text-[12.5px]">{r.number}</td>
                    <td>{fmtDate(r.date)}</td>
                    <td>
                      <span className="pill pill-info">{r.expense_head || "—"}</span>
                      {r.salary_month && (
                        <div className="text-[10.5px] text-muted-foreground mt-0.5 font-mono">
                          {new Date(r.salary_month + "-01").toLocaleDateString("en-IN", { month: "short", year: "numeric" })} salary
                        </div>
                      )}
                    </td>
                    <td className="font-medium">{r.party_name}</td>
                    <td><span className="pill pill-muted">{r.mode}</span></td>
                    <td className="font-mono text-[12px] text-muted-foreground">{r.reference || "—"}</td>
                    <td className="num font-semibold">{inr(r.amount)}</td>
                    <td className="num" style={{ textAlign: "right" }}>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)} data-testid={`expense-del-${r.id}`}>
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {tab === "expense" && filteredRows.length > 0 && (
                <tfoot className="bg-muted/30">
                  <tr>
                    <td colSpan={8} className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold py-2 px-3">
                      Totals by payee ({payeeTotals.length} unique)
                    </td>
                  </tr>
                  {payeeTotals.map((p) => (
                    <tr key={`total-${p.name}`} data-testid={`expense-payee-total-${p.name}`}>
                      <td colSpan={3} className="font-medium">↳ {p.name}</td>
                      <td colSpan={2} className="text-[11px] text-muted-foreground">
                        {Object.entries(p.byHead).map(([h, v], i) => (
                          <span key={h} className="mr-2">
                            {i > 0 && "· "}
                            <span className="font-medium">{h}</span> {inr(v)}
                          </span>
                        ))}
                      </td>
                      <td className="text-[11px] text-muted-foreground">{p.count} voucher{p.count > 1 ? "s" : ""}</td>
                      <td className="num font-semibold">{inr(p.total)}</td>
                      <td></td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-border">
                    <td colSpan={6} className="font-semibold py-2">Grand Total</td>
                    <td className="num font-bold text-base">{inr(payeeTotals.reduce((s, p) => s + p.total, 0))}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </TabsContent>

      </Tabs>
      {editing && (
        <EditVoucherDialog voucher={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}
